/**
 * donations-list.ts
 *
 * Handles the AJAX behaviour for the Donations list pages:
 * - Intercepts pagination link clicks inside #donationsList
 * - Fetches the partial view with X-Requested-With: XMLHttpRequest header
 * - Replaces #donationsList innerHTML with the returned HTML
 * - Keeps the browser URL in sync with history.replaceState
 * - Progressively wires #searchTerm (300ms debounce), #statusFilter (immediate),
 *   and #schoolFilter (immediate) when present on the page.
 *   School Portal uses search + status; Org Portal adds school filter; User Portal is no-op.
 * - Toggles #clearFiltersContainer visibility based on active filters.
 *
 * Mirrors the pattern established in disbursements-list.ts.
 */

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let activeController: AbortController | null = null;

/**
 * Fetches the donations list partial from the server and replaces
 * the #donationsList div content.
 * @param url - The request URL including query-string parameters.
 */
async function fetchDonationsList(url: string): Promise<void> {
  if (activeController) {
    activeController.abort();
  }

  activeController = new AbortController();

  const listEl = document.getElementById('donationsList');
  if (!listEl) return;

  try {
    const response = await fetch(url, {
      signal: activeController.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) {
      console.error(`Donations list fetch failed: ${response.status}`);
      return;
    }

    const html = await response.text();
    listEl.innerHTML = html;

    // Sync the browser address bar without a page reload
    window.history.replaceState(null, '', url);

    // Re-attach pagination listeners after DOM replacement
    attachPaginationListeners();
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return;
    }
    console.error('Unexpected error loading donations list:', err);
  }
}

/**
 * Builds a URL for the donations list with the current filter values.
 * Always resets pageNumber to 1 when called from a filter change.
 */
function buildListUrl(): string {
  const listEl = document.getElementById('donationsList');
  const baseUrl = listEl?.dataset['listUrl'] ?? window.location.pathname;

  const searchInput  = document.getElementById('searchTerm')   as HTMLInputElement  | null;
  const statusSelect = document.getElementById('statusFilter') as HTMLSelectElement | null;
  const schoolSelect = document.getElementById('schoolFilter') as HTMLSelectElement | null;

  const params = new URLSearchParams();
  const search   = searchInput?.value.trim() ?? '';
  const status   = statusSelect?.value ?? '';
  const schoolId = schoolSelect?.value ?? '';

  if (search)   params.set('searchTerm', search);
  if (status)   params.set('status', status);
  if (schoolId) params.set('schoolId', schoolId);

  // Always reset to page 1 when a filter changes
  params.set('pageNumber', '1');

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Returns true when at least one filter input has a non-empty value.
 */
function hasActiveFilters(): boolean {
  const searchInput  = document.getElementById('searchTerm')   as HTMLInputElement  | null;
  const statusSelect = document.getElementById('statusFilter') as HTMLSelectElement | null;
  const schoolSelect = document.getElementById('schoolFilter') as HTMLSelectElement | null;

  return (searchInput?.value.trim() ?? '') !== ''
      || (statusSelect?.value        ?? '') !== ''
      || (schoolSelect?.value         ?? '') !== '';
}

/**
 * Shows or hides the clear-filters button based on whether any filter is active.
 */
function updateClearButton(active: boolean): void {
  const container = document.getElementById('clearFiltersContainer');
  if (!container) return;
  container.classList.toggle('d-none', !active);
}

/**
 * Attaches click listeners to all pagination links within #donationsList.
 * Each link's server-rendered href (which includes current filter params) is
 * used directly for the AJAX fetch.
 */
function attachPaginationListeners(): void {
  const listEl = document.getElementById('donationsList');
  if (!listEl) return;

  const paginationLinks = listEl.querySelectorAll<HTMLAnchorElement>('.pagination .page-link');
  for (const link of paginationLinks) {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const href = link.getAttribute('href');
      if (href) {
        void fetchDonationsList(href);
      }
    });
  }
}

/**
 * Initialises the donations list AJAX behaviour.
 * Call once after the DOM is ready.
 * No-op when the required #donationsList element is absent from the page.
 *
 * Progressively wires search and status filter controls when they are present
 * on the page (School Portal). When absent (User Portal), the search/filter
 * logic is a no-op and only pagination interception is active.
 */
export function initDonationsList(): void {
  const listEl = document.getElementById('donationsList');
  if (!listEl) return;

  attachPaginationListeners();

  // Progressive enhancement: search input
  const searchInput = document.getElementById('searchTerm') as HTMLInputElement | null;
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        updateClearButton(hasActiveFilters());
        void fetchDonationsList(buildListUrl());
      }, 300);
    });
  }

  // Progressive enhancement: status filter dropdown
  const statusSelect = document.getElementById('statusFilter') as HTMLSelectElement | null;
  if (statusSelect) {
    statusSelect.addEventListener('change', () => {
      updateClearButton(hasActiveFilters());
      void fetchDonationsList(buildListUrl());
    });
  }

  // Progressive enhancement: school filter dropdown (Org Portal only)
  const schoolSelect = document.getElementById('schoolFilter') as HTMLSelectElement | null;
  if (schoolSelect) {
    schoolSelect.addEventListener('change', () => {
      updateClearButton(hasActiveFilters());
      void fetchDonationsList(buildListUrl());
    });
  }

  // Progressive enhancement: clear filters button
  const clearBtn = document.getElementById('clearFiltersContainer')?.querySelector('a');
  if (clearBtn) {
    clearBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (searchInput) searchInput.value = '';
      if (statusSelect) statusSelect.value = '';
      if (schoolSelect) schoolSelect.value = '';
      updateClearButton(false);
      void fetchDonationsList(buildListUrl());
    });
  }
}
